/**
 * FILE-OVERVIEW:
 * - 役割: モバイル用の下部固定ナビゲーションの見た目。
 * - 主な連携: Calendar/MobileNavigation.tsx, Memo/MemoNavigation.tsx
 * - 本番運用上の注意: 固定位置UIのため、変更時はカレンダー高さと画面下部の
 *   被りを両画面で確認する。位置とサイズは CSS 変数
 *   --mobile-footer-nav-bottom / --mobile-footer-nav-height に依存する。
 */

import React from 'react';
import { Box, IconButton, Typography } from '@mui/material';

/** 下部ナビの項目1つ */
export interface BottomNavItem {
  key: string;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  /** 選択中として強調するか */
  isActive: boolean;
}

interface BottomNavBarProps {
  items: BottomNavItem[];
  /** 画面ごとのスタイル指定に使うクラス名 */
  className: string;
}

const getActionButtonSx = (isActive: boolean) => ({
  width: 36,
  height: 36,
  color: isActive ? '#1a73e8' : '#5f6368',
  bgcolor: isActive ? '#e8f0fe' : 'transparent',
  borderRadius: 2,
  transition: 'all 0.2s ease',
  '&:hover': {
    bgcolor: isActive ? '#dce8fd' : '#f1f3f4',
  },
  '&:active': {
    transform: 'scale(0.96)',
  },
});

const BottomNavBar = ({ items, className }: BottomNavBarProps) => {
  return (
    <Box
      className={className}
      sx={{
        position: 'fixed',
        bottom: 'var(--mobile-footer-nav-bottom)',
        left: 12,
        right: 12,
        height: 'var(--mobile-footer-nav-height)',
        zIndex: 20,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: 'rgba(255,255,255,0.96)',
        border: '1px solid #e5e9f0',
        boxShadow: '0 6px 18px rgba(60,64,67,0.22), 0 2px 8px rgba(60,64,67,0.14)',
        borderRadius: 4,
        px: 0.75,
        py: 0.375,
        boxSizing: 'border-box',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
      }}
    >
      {items.map((item) => (
        <Box
          key={item.key}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            flex: 1,
            minWidth: 0,
          }}
        >
          <IconButton
            onClick={item.onClick}
            sx={getActionButtonSx(item.isActive)}
            aria-label={item.label}
          >
            {item.icon}
          </IconButton>
          <Typography
            sx={{
              fontSize: '10px',
              fontWeight: item.isActive ? 600 : 500,
              color: item.isActive ? '#1a73e8' : '#5f6368',
              mt: 0.25,
              lineHeight: 1.2,
            }}
          >
            {item.label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

export default BottomNavBar;
